import { jsxs, jsx } from 'react/jsx-runtime';
import { Dialog, DialogPanel } from '@headlessui/react';
import { Stack } from '@mui/material';
import { clsx } from 'clsx';
import { forwardRef, useMemo, useRef, useState } from 'react';
import { Button } from '../Button/Button.js';
import { Heading } from '../Heading/Heading.js';
import { Text } from '../Text/Text.js';
import styles from './Prompt.module.css.js';

function Prompt({ title, onConfirm, onCancel, promptType = "confirm", description, caption, confirmButton, cancelButton, defaultValue, placeholder, required, validate, }) {
    const inputRef = useRef(null);
    const [value, setValue] = useState(defaultValue);
    const validations = useBuildValidations(validate, required);
    const { valid } = useValidate(value, validations);
    const [isOpen, setIsOpen] = useState(true);
    const handleConfirm = () => {
        if (valid) {
            setIsOpen(false);
            onConfirm(value);
        }
    };
    const confirmButtonArgs = Object.assign(Object.assign({ label: "Confirm", prominence: "primary" }, confirmButton), { onClick: handleConfirm });
    const cancelButtonArgs = Object.assign(Object.assign({ label: "Cancel", prominence: "secondary" }, cancelButton), { onClick: onCancel });
    return (jsxs(Dialog, { className: styles.Container, onClose: () => {
            onCancel();
            setIsOpen(false);
        }, open: isOpen, initialFocus: inputRef, children: [jsx("div", { className: styles.Overlay, "aria-hidden": "true" }), jsx(DialogPanel, { className: styles.Dialog, children: jsxs(Stack, { justifyContent: "space-evenly", spacing: 2, children: [jsx(Heading, { level: "4", as: "h2", multiline: true, children: title }), description ?
                            jsx(Text, { prominence: "secondary", children: description })
                            : null, caption ?
                            jsx(Text, { prominence: "secondary", variant: "caption", weight: "bold", children: caption })
                            : null, promptType === "input" && (jsx(PromptInput, { handleConfirm: handleConfirm, placeholder: placeholder, required: required, setValue: setValue, validations: validations, value: value, ref: inputRef })), jsxs(Stack, { direction: "row", justifyContent: "end", spacing: 1, children: [jsx(Button, Object.assign({}, cancelButtonArgs)), jsx(Button, Object.assign({}, confirmButtonArgs))] })] }) })] }));
}
const PromptInput = forwardRef(function PromptInput({ value, placeholder, required, setValue, handleConfirm, validations }, ref) {
    const { valid, errorMessage } = useValidate(value, validations);
    const inputClass = clsx(styles.input, { [styles.inputError]: !valid });
    return (jsxs(Stack, { spacing: 0.5, children: [jsx("input", { "aria-label": "prompt input", className: inputClass, onChange: (event) => {
                    setValue(event.target.value);
                }, onKeyDown: (event) => {
                    if (event.key === "Enter") {
                        event.preventDefault();
                        handleConfirm();
                    }
                }, placeholder: placeholder, required: required, type: "text", value: value !== null && value !== void 0 ? value : "", ref: ref }), errorMessage ?
                jsx(Text, { variant: "caption", accent: "error", children: errorMessage })
                : null] }));
});
function useValidate(value, validations) {
    return useMemo(() => {
        for (const validation of validations) {
            const result = validation(value);
            if (result)
                return { valid: false, errorMessage: result };
        }
        return { valid: true, errorMessage: undefined };
    }, [validations, value]);
}
function useBuildValidations(validate, required) {
    return useMemo(() => {
        const validations = [];
        if (validate)
            validations.push(validate);
        if (required)
            validations.push((value) => !(value === null || value === void 0 ? void 0 : value.trim()) && value !== undefined ? "Required" : undefined);
        return validations;
    }, [required, validate]);
}

export { Prompt };
//# sourceMappingURL=Prompt.js.map
